import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Shield, AlertTriangle, Eye, RefreshCw, Clock } from "lucide-react";
import { Layout } from "@/components/Layout";

interface AuditLog {
  id: string;
  accessed_by: string;
  accessed_staff_id: string;
  action_type: string;
  timestamp: string;
  ip_address: string | null;
  user_agent: string | null;
}

interface StaffMember {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role_level: 'user' | 'admin' | 'superadmin' | 'adviser';
}

const SecurityDashboard = () => {
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [currentUser, setCurrentUser] = useState<StaffMember | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchCurrentUser();
    fetchAuditLogs();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('staff_member_id')
        .eq('user_id', user.id)
        .single();

      if (profile) {
        const { data: staffMember } = await supabase
          .from('staff_members')
          .select('id, first_name, last_name, email, role_level')
          .eq('id', profile.staff_member_id)
          .single();
        
        setCurrentUser(staffMember);
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const { data, error } = await supabase
        .from('staff_access_audit')
        .select('*')
        .order('timestamp', { ascending: false })
        .limit(100);
      
      if (error) throw error;
      setAuditLogs((data || []).map(log => ({
        ...log,
        ip_address: log.ip_address?.toString() || null,
        user_agent: log.user_agent || null
      })));
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast({
        title: "Error",
        description: "Failed to load audit logs",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getActionBadge = (action: string) => {
    switch (action) {
      case 'view':
        return <Badge variant="secondary"><Eye className="w-3 h-3 mr-1" />View</Badge>;
      case 'update':
        return <Badge variant="default">Update</Badge>;
      case 'create':
        return <Badge variant="default">Create</Badge>;
      case 'delete':
        return <Badge variant="destructive">Delete</Badge>;
      default:
        return <Badge variant="outline">{action}</Badge>;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  if (loading) {
    return <Layout><div className="flex justify-center items-center h-64">Loading...</div></Layout>;
  }

  if (currentUser?.role_level !== 'admin' && currentUser?.role_level !== 'superadmin') {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <Card className="p-8 text-center">
            <Shield className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
            <p className="text-muted-foreground">You don't have permission to access the security dashboard.</p>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Security Dashboard</h1>
          <p className="text-muted-foreground mt-2">Monitor staff access and security events</p>
        </div>
        <Button onClick={fetchAuditLogs} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Security Status */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Security Status</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">Secure</div>
            <p className="text-xs text-muted-foreground">
              Row-level security enabled
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Audit Logs</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{auditLogs.length}</div>
            <p className="text-xs text-muted-foreground">
              Access events recorded
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Recent Activity</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {auditLogs.filter(log => 
                new Date(log.timestamp) > new Date(Date.now() - 24 * 60 * 60 * 1000)
              ).length}
            </div>
            <p className="text-xs text-muted-foreground">
              Events in last 24h
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Security Recommendations */}
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Security Recommendations</AlertTitle>
        <AlertDescription className="mt-2">
          <ul className="list-disc list-inside space-y-1">
            <li>Enable leaked password protection in Supabase Auth settings</li>
            <li>Regularly review audit logs for suspicious activity</li>
            <li>Consider implementing 2FA for admin accounts</li>
            <li>Rotate API keys periodically</li>
          </ul>
        </AlertDescription>
      </Alert>

      {/* Audit Logs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Staff Access Audit Logs
          </CardTitle>
          <CardDescription>
            Recent staff member access events and administrative actions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Timestamp</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Accessed Staff ID</TableHead>
                <TableHead>IP Address</TableHead>
                <TableHead>User Agent</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {auditLogs.slice(0, 50).map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="font-mono text-sm">
                    {formatTimestamp(log.timestamp)}
                  </TableCell>
                  <TableCell>{getActionBadge(log.action_type)}</TableCell>
                  <TableCell className="font-mono text-sm">
                    {log.accessed_staff_id === null ? 'All Staff' : (log.accessed_staff_id?.slice(0, 8) || 'N/A')}
                  </TableCell>
                  <TableCell className="font-mono text-sm">{log.ip_address || 'N/A'}</TableCell>
                  <TableCell className="text-sm max-w-xs truncate" title={log.user_agent || ''}>
                    {log.user_agent || 'N/A'}
                  </TableCell>
                </TableRow>
              ))}
              {auditLogs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground">
                    No audit logs found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
    </Layout>
  );
};

export default SecurityDashboard;